<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, onMounted } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useContactDetails } from '../../composables/useContactDetails'
import { GetTermsAndConditionsPageUrl } from '../../stores/SettingsStore'
import OrderHistoryScreen from './OrderHistoryScreen.vue'
import BillingAddressScreen from './BillingAddressScreen.vue'
import ContactDetailsScreen from './ContactDetailsScreen.vue'
import PrivacyPolicyScreen from './PrivacyPolicyScreen.vue'
import MarketingOptInScreen from './MarketingOptInScreen.vue'
import PersonalDataScreen from './PersonalDataScreen.vue'
import {
  faClockRotateLeft,
  faEnvelope,
  faLock,
  faFileContract,
  faShieldAlt,
  faInfoCircle,
  faArrowLeft,
  faBullhorn
} from '@fortawesome/free-solid-svg-icons'

const { t } = useI18n()

// Navigation state
const currentView = ref<string>('main')
const selectedOption = ref<string>('')
const termsAndConditionsPageUrl = ref<string>('')

// Get composables
const { loadContactDetailsData } = useContactDetails()

// Account management options
const accountOptions = [
  {
    id: 'order-history',
    icon: faClockRotateLeft,
    title: 'accountInfo.orderHistory.title',
    description: 'accountInfo.orderHistory.description',
    iconClass: 'order-history-icon'
  },
  {
    id: 'billing-address',
    icon: faEnvelope,
    title: 'accountInfo.billingAddress.title',
    description: 'accountInfo.billingAddress.description',
    iconClass: 'billing-address-icon'
  },
  {
    id: 'contact-details',
    icon: faLock,
    title: 'accountInfo.contactDetails.title',
    description: 'accountInfo.contactDetails.description',
    iconClass: 'contact-details-icon'
  },
  {
    id: 'terms-conditions',
    icon: faFileContract,
    title: 'accountInfo.termsConditions.title',
    description: 'accountInfo.termsConditions.description',
    iconClass: 'terms-conditions-icon'
  },
  {
    id: 'data-provisions',
    icon: faShieldAlt,
    title: 'accountInfo.dataProvisions.title',
    description: 'accountInfo.dataProvisions.description',
    iconClass: 'data-provisions-icon'
  },
  {
    id: 'marketing-opt-in',
    icon: faBullhorn,
    title: 'accountInfo.marketingOptIn.title',
    description: 'accountInfo.marketingOptIn.description',
    iconClass: 'marketing-opt-in-icon'
  },
  {
    id: 'personal-data',
    icon: faInfoCircle,
    title: 'accountInfo.personalData.title',
    description: 'accountInfo.personalData.description',
    iconClass: 'personal-data-icon'
  }
]

async function selectOption(optionId: string) {
  selectedOption.value = optionId
  currentView.value = 'update'

  // Load data based on selected option
  if (optionId === 'contact-details') {
    await loadContactDetailsData()
  }
  // Billing address data loading is handled by the BillingAddressScreen component
}

function goBack() {
  currentView.value = 'main'
  selectedOption.value = ''
}

function getCurrentOptionData() {
  return accountOptions.find(option => option.id === selectedOption.value)
}

onMounted(async () => {
  termsAndConditionsPageUrl.value = await GetTermsAndConditionsPageUrl()
})
</script>

<template>
  <div class="account-info-container">
    <!-- Main Options View -->
    <div v-if="currentView === 'main'" class="main-view">
      <div class="page-header">
        <h1 class="page-title">{{ t('accountInfo.title') }}</h1>
      </div>

      <div class="options-grid">
        <div
          v-for="option in accountOptions"
          :key="option.id"
          class="option-card"
          @click="selectOption(option.id)"
        >
          <div class="option-icon">
            <FontAwesomeIcon :icon="option.icon" :class="option.iconClass" />
          </div>
          <div class="option-content">
            <h3 class="option-title">{{ t(option.title) }}</h3>
            <p class="option-description">{{ t(option.description) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Update Screen View -->
    <div v-if="currentView === 'update'" class="update-view">
      <div class="update-header">
        <button @click="goBack" class="back-button">
          <FontAwesomeIcon :icon="faArrowLeft" />
          {{ t('buttons.Back') }}
        </button>
        <h2 class="update-title">{{ getCurrentOptionData()?.title ? t(getCurrentOptionData()!.title) : '' }}</h2>
      </div>

      <div class="update-content">
        <!-- Order History Update Screen -->
        <OrderHistoryScreen v-if="selectedOption === 'order-history'" />

        <!-- Billing Address Update Screen -->
        <BillingAddressScreen v-if="selectedOption === 'billing-address'" />

        <!-- Contact Details Update Screen -->
        <ContactDetailsScreen v-if="selectedOption === 'contact-details'" />

        <!-- Terms & Conditions Update Screen -->
        <div v-if="selectedOption === 'terms-conditions'" class="update-section">
          <div class="iframe-container">
            <iframe
              :src="termsAndConditionsPageUrl"
              class="terms-iframe"
              :title="t('accountInfo.termsConditions.title')"
              frameborder="0"
              scrolling="auto"
            ></iframe>
          </div>
        </div>

        <!-- PrivacyPolicyScreen Screen -->
        <PrivacyPolicyScreen v-if="selectedOption === 'data-provisions'" />

        <!-- Marketing Opt In Update Screen -->
        <MarketingOptInScreen v-if="selectedOption === 'marketing-opt-in'" />

        <!-- Personal Data Update Screen -->
        <PersonalDataScreen v-if="selectedOption === 'personal-data'" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.account-info-container {
  width: 100%;
  padding: $card-padding;
  background-color: var(--vt-c-white-soft);
  min-height: 100vh;
}

/* Main View Styles */
.main-view {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 30px;
}

.page-title {
  @include typography-h2;
  @include font-weight-semibold;
  color: #333;
  margin: 0;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: $spacing-5;
  margin-bottom: 20px;
}

.option-card {
  background: white;
  border-radius: $radius-lg;
  padding: $spacing-6;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: flex-start;
  gap: $gap-md;
  border: 2px solid transparent;
}

.option-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(black, 0.2);
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.option-icon {
  width: 48px;
  height: 48px;
  border-radius: $radius-md;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9f5f7;
  flex-shrink: 0;
}

.option-icon svg {
  @include typography-h4;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.option-content {
  flex: 1;
}

.option-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 8px 0;
}

.option-description {
  @include typography-small;
  color: #666;
  margin: 0;
}

/* Update View Styles */
.update-view {
  max-width: 800px;
  margin: 0 auto;
}

.update-header {
  display: flex;
  align-items: center;
  gap: $gap-md;
  margin-bottom: 30px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  padding: $spacing-2 $spacing-4;
  background: white;
  border: 1px solid #e0e0e0;
  @include typography-small;
  border-radius: $radius-md;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.update-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin: 0;
}

.update-content {
  background: white;
  border-radius: $radius-lg;
  padding: $spacing-8;
  box-shadow: 0 2px 8px rgba(black, 0.1);
}

.update-section {
  width: 100%;
}

.coming-soon {
  text-align: center;
  padding: $spacing-10 $spacing-5;
}

.coming-soon-icon {
  @include typography-display;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  margin-bottom: 16px;
}

.coming-soon h3 {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 12px 0;
}

.coming-soon p {
  @include typography-body;
  color: #666;
  margin: 0;
}

/* Terms & Conditions iframe styles */
.iframe-container {
  width: 100%;
  height: 600px;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
  background: white;
}

.terms-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* Icon specific colors */
.order-history-icon {
  color: #28a745 !important;
}

.billing-address-icon {
  color: #007bff !important;
}

.contact-details-icon {
  color: #ffc107 !important;
}

.terms-conditions-icon {
  color: #6f42c1 !important;
}

.data-provisions-icon {
  color: #20c997 !important;
}

.marketing-opt-in-icon {
  color: #e91e63 !important;
}

.personal-data-icon {
  color: #fd7e14 !important;
}

/* Error styling */
.form-input.error,
.form-select.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 2px rgba(#dc2626, 0.1);
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 5px;
  display: block;
}

.password-pattern-div {
  margin-top: 8px;
  @include typography-small;
  padding: $spacing-3;
  border-radius: $radius-sm;
}

.bg-light {
  background-color: var(--vt-c-white-soft);
  border: 1px solid #dee2e6;
}

/* Address Styles */
.billing-address-section {
  padding: $card-padding;
}

.address-display-section {
  margin-bottom: 2rem;
}

.address-type-section {
  margin-bottom: 2rem;
}

.address-type-section h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 1rem;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-selection-view {
  width: 100%;
}

.address-actions {
  display: flex;
  gap: $gap-sm;
}

.me-2 {
  margin-right: 0.5rem;
}

.selection-header {
  display: flex;
  align-items: center;
  gap: $gap-md;
  margin-bottom: 20px;
}

.selection-header h3 {
  @include typography-h4;
  margin: 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-list h4 {
  @include font-weight-semibold;
  margin-bottom: 1rem;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  background-color: var(--vt-c-white-soft);
  margin-bottom: 1rem;
  color: #000;
}

.address-item:last-child {
  margin-bottom: 0;
}

.address-item.selectable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.address-item.selectable:hover {
  background-color: #e9f5f7;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(var(--vt-c-teal-dark), 0.15);
}

.no-addresses {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  background-color: var(--vt-c-white-soft);
}

.add-new-section {
  margin-top: 1rem;
  text-align: center;
}

.default-badge {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  @include typography-caption;
  @include font-weight-medium;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-lg;
  margin-left: 8px;
}

.loading-message {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
}

.address-display {
  flex: 1;
}

.address-display p {
  margin: 0;
  line-height: 1.5;
}

.no-address-text {
  color: #666;
  font-style: italic;
}

.address-actions {
  margin-left: 1rem;
  display: flex;
  gap: 0.5rem;
}

.address-form {
  padding: 1rem;
  border: 1px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-radius: $radius-md;
  background-color: #fefefe;
}

.address-form h5 {
  margin-bottom: 1rem;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-control {
  margin-bottom: 1rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.form-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.form-button:disabled:hover {
  background-color: #6c757d;
  transform: none;
}

/* Contact Details Form Styles */
.contact-details-form {
  width: 100%;
}

.form-section {
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  padding: $spacing-6;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
}

.section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 20px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  @include typography-small;
  @include font-weight-medium;
  display: block;
  color: #333;
  margin-bottom: 6px;
}

.form-input,
.form-select {
  width: 100%;
  padding: $spacing-2-5 $spacing-3;
  @include typography-small;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  background: white;
  transition: border-color 0.2s ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 0 0 2px rgba(var(--vt-c-teal-dark), 0.1);
}

.form-row {
  display: flex;
  gap: $gap-md;
  align-items: flex-end;
}

.form-group.half-width {
  flex: 1;
}

.form-button {
  padding: $spacing-2-5 $spacing-6;
  @include typography-small;
  @include font-weight-medium;
  border: none;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;
}

.form-button.primary {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
}

.form-button.primary:hover {
  background-color: #145258;
  transform: translateY(-1px);
}

.form-button.secondary {
  background-color: #6c757d;
  color: white;
}

.form-button.secondary:hover {
  background-color: #5a6268;
  transform: translateY(-1px);
}

.security-question-info {
  background: #e9f5f7;
  padding: $spacing-3;
  border-radius: $radius-sm;
  margin-bottom: 16px;
}

.security-question-info p {
  @include typography-small;
  margin: 0;
  color: #666;
}

/* Responsive Design */
@media (max-width: 768px) {
  .account-info-container {
    padding: $spacing-4;
  }

  .options-grid {
    grid-template-columns: 1fr;
    gap: $gap-md;
    justify-items: stretch;
    max-width: none;
    margin: 0;
  }

  .option-card {
    padding: $card-padding;
    width: 100%;
  }

  .page-title {
    @include typography-h3;
  }

  .update-title {
    @include typography-h3;
  }

  .update-content {
    padding: $spacing-6;
  }

  .update-header {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-3;
  }

  .form-section {
    padding: $card-padding;
  }

  .form-row {
    flex-direction: column;
    gap: $spacing-3;
  }

  .form-group.half-width {
    flex: none;
  }
}

@media (max-width: 768px) {
  .option-card {
    justify-content: center;
    text-align: center;
  }

  .option-icon {
    align-self: center;
  }

  .option-content {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .option-card {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }

  .option-icon {
    align-self: center;
  }

  .form-section {
    padding: $spacing-4;
  }

  .form-button {
    width: 100%;
  }
}

/* Address Verification Backdrop */
.address-verification-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(black, 0.5) !important;
  z-index: $z-modal-backdrop !important;
  pointer-events: all !important;
  cursor: default !important;
  display: block !important;
}

/* Ensure the address verification popup appears above the backdrop */
:deep(.address-val-popup-content) {
  z-index: $z-modal !important;
}

/* Also target the Kendo popup wrapper that might have a different class */
:deep(.k-popup) {
  z-index: $z-modal !important;
}
</style>
